import React from 'react';
import { Box, Typography, Alert } from '@mui/material';
import { useLicense } from '../contexts/LicenseContext';
import { useBrewery } from '../contexts/BreweryContext';

interface PermissionGuardProps {
  viewKey: string;
  requireWrite?: boolean;
  children: React.ReactNode;
}

const PermissionGuard: React.FC<PermissionGuardProps> = ({ viewKey, requireWrite = false, children }) => {
  const { isBreweryAdmin, isBreweryMember } = useLicense();

  // Non-brewery users (homebrewers) always pass
  if (!isBreweryMember) {
    return <>{children}</>;
  }

  // Admin always passes
  if (isBreweryAdmin) {
    return <>{children}</>;
  }

  // For brewery members, check permissions via BreweryContext
  return (
    <BreweryPermissionCheck viewKey={viewKey} requireWrite={requireWrite}>
      {children}
    </BreweryPermissionCheck>
  );
};

// Separate component that uses useBrewery (only rendered when inside BreweryProvider)
const BreweryPermissionCheck: React.FC<PermissionGuardProps> = ({ viewKey, requireWrite = false, children }) => {
  const { hasViewAccess, hasWriteAccess } = useBrewery();

  if (!hasViewAccess(viewKey)) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          <Typography variant="body1">
            You don't have access to this view. Contact your brewery admin for permissions.
          </Typography>
        </Alert>
      </Box>
    );
  }

  if (requireWrite && !hasWriteAccess(viewKey)) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          <Typography variant="body1">
            You have read-only access to this view.
          </Typography>
        </Alert>
        {children}
      </Box>
    );
  }

  return <>{children}</>;
};

export default PermissionGuard;
